import QRCode from "qrcode";
import { RECEIPT_LOGO_DATA_URI } from "./receiptLogo";

/**
 * Kvittoutskrift för mobil-POS mot en BLE-termoskrivare (55/58 mm) via Web Bluetooth.
 * Renderar kvittot till en 384px-canvas (logo, text, QR) och skickar det som
 * ESC/POS-raster (GS v 0) – "på samma sätt som gamla posappen".
 *
 * OBS: Web Bluetooth stöds på Android (Chrome/Edge) och desktop, INTE på iOS.
 */

export type ReceiptLine = {
    qty: number;
    productName: string;
    variantName?: string;
    priceGroupName?: string;
    occationName?: string;
    itemPrice: number;
};

export type ReceiptData = {
    publicId: string;
    orderId: string;       // kodas som QR
    date: Date;
    lines: ReceiptLine[];
    totalSum: number;
    vatPercentage: number;
    totalVat: number;
};

const PRINTER_WIDTH = 384; // 58mm @ 203dpi (48mm skrivbart)

// Vanliga GATT-tjänster för billiga ESC/POS BLE-skrivare (för optionalServices)
const KNOWN_SERVICES: (number | string)[] = [
    0x18f0,
    "000018f0-0000-1000-8000-00805f9b34fb",
    "49535343-fe7d-4ae5-8fa9-9fafd205e455",
    "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
    "0000ff00-0000-1000-8000-00805f9b34fb",
    "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
];

let device: any = null;
let writeChar: any = null;

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

export const isPrinterSupported = (): boolean => {
    return typeof navigator !== "undefined" && !!(navigator as any).bluetooth;
};

export const getConnectedPrinterName = (): string | null => {
    return device && device.gatt && device.gatt.connected ? (device.name || "Skrivare") : null;
};

const findWritableCharacteristic = async (server: any): Promise<any> => {
    const services = await server.getPrimaryServices();
    for (const svc of services) {
        let chars: any[] = [];
        try { chars = await svc.getCharacteristics(); } catch { continue; }
        for (const c of chars) {
            if (c.properties && (c.properties.write || c.properties.writeWithoutResponse)) {
                return c;
            }
        }
    }
    return null;
};

/** Öppnar webbläsarens enhetsväljare och ansluter till en skrivare. */
export const connectPrinter = async (): Promise<string> => {
    if (!isPrinterSupported()) {
        throw new Error("Den här enheten stödjer inte Web Bluetooth (fungerar på Android, inte iPhone).");
    }
    device = await (navigator as any).bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: KNOWN_SERVICES,
    });
    device.addEventListener("gattserverdisconnected", () => { writeChar = null; });
    const server = await device.gatt.connect();
    writeChar = await findWritableCharacteristic(server);
    if (!writeChar) {
        throw new Error("Hittade ingen skrivbar egenskap på skrivaren.");
    }
    return device.name || "Skrivare";
};

export const disconnectPrinter = (): void => {
    try { if (device && device.gatt && device.gatt.connected) { device.gatt.disconnect(); } } catch { /* ignore */ }
    device = null;
    writeChar = null;
};

const ensureConnected = async (): Promise<void> => {
    if (!device) {
        throw new Error("Ingen skrivare ansluten. Anslut skrivare i inställningarna.");
    }
    if (!device.gatt.connected || !writeChar) {
        const server = await device.gatt.connect();
        writeChar = await findWritableCharacteristic(server);
        if (!writeChar) { throw new Error("Kunde inte återansluta till skrivaren."); }
    }
};

const writeBytes = async (bytes: Uint8Array): Promise<void> => {
    const noResp = !!(writeChar.properties && writeChar.properties.writeWithoutResponse);
    const CHUNK = 180;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        const slice = bytes.slice(i, i + CHUNK);
        if (noResp && writeChar.writeValueWithoutResponse) {
            await writeChar.writeValueWithoutResponse(slice);
        } else {
            await writeChar.writeValue(slice);
        }
        await sleep(18);
    }
};

// ---------- Canvas-rendering ----------

const loadImage = (src: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

/** Renderar kvittot till en canvas och returnerar den beskurna till använd höjd. */
const buildReceiptCanvas = async (data: ReceiptData): Promise<HTMLCanvasElement> => {
    const W = PRINTER_WIDTH;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = 2400; // rikligt tilltaget, beskärs sedan
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, W, canvas.height);
    ctx.fillStyle = "#000";
    ctx.textBaseline = "top";

    let y = 8;
    const left = 8;
    const right = W - 8;

    const line = (text: string, opts?: { font?: string; align?: "left" | "center" | "right"; x?: number; adv?: boolean }) => {
        const font = opts?.font || "22px sans-serif";
        const align = opts?.align || "left";
        ctx.font = font;
        ctx.textAlign = align;
        const x = opts?.x ?? (align === "center" ? W / 2 : align === "right" ? right : left);
        ctx.fillText(text, x, y);
        if (opts?.adv !== false) {
            const m = ctx.measureText("Ág");
            const h = (m.actualBoundingBoxAscent || 18) + (m.actualBoundingBoxDescent || 6) + 6;
            y += Math.max(h, 22);
        }
    };
    const gap = (px: number) => { y += px; };

    // Logo
    try {
        const logo = await loadImage(RECEIPT_LOGO_DATA_URI);
        const lw = 220;
        const lh = Math.round(lw * logo.height / logo.width);
        ctx.drawImage(logo, (W - lw) / 2, y, lw, lh);
        y += lh + 10;
    } catch { /* logo valfri */ }

    line("KVITTO PÅ KÖP", { font: "bold 26px sans-serif", align: "center" });
    gap(6);
    line(`Order-id: ${data.publicId}`, { font: "20px sans-serif" });
    line(`Datum: ${data.date.toLocaleString("sv-SE")}`, { font: "20px sans-serif" });
    gap(8);
    ctx.fillRect(left, y, W - 2 * left, 2); gap(10);

    // Rader
    for (const it of data.lines) {
        ctx.textAlign = "left";
        ctx.font = "bold 21px sans-serif";
        ctx.fillText(`${it.qty}x ${it.productName}`, left, y);
        ctx.textAlign = "right";
        ctx.font = "21px sans-serif";
        ctx.fillText(`${it.qty * it.itemPrice} kr`, right, y);
        y += 26;
        const sub = [it.variantName, it.priceGroupName && it.priceGroupName !== "Normal" ? `(${it.priceGroupName})` : "", it.occationName]
            .filter(Boolean).join(" ");
        if (sub) { line(sub, { font: "19px sans-serif", x: left + 14 }); }
        gap(4);
    }

    gap(6);
    ctx.fillRect(left, y, W - 2 * left, 2); gap(10);

    // Total + moms
    ctx.textAlign = "left"; ctx.font = "bold 24px sans-serif";
    ctx.fillText("TOTALT", left, y);
    ctx.textAlign = "right";
    ctx.fillText(`${data.totalSum} kr`, right, y);
    y += 30;
    line(`Varav moms (${data.vatPercentage}%): ${data.totalVat} kr`, { font: "18px sans-serif", align: "right" });
    gap(16);

    // QR av order-id
    try {
        const qrUrl = await QRCode.toDataURL(data.orderId, { margin: 1, width: 220 });
        const qr = await loadImage(qrUrl);
        ctx.drawImage(qr, (W - 220) / 2, y, 220, 220);
        y += 230;
    } catch { /* QR valfri */ }

    // Fot
    const footer = [
        "VASALOPPSFÖRENINGEN SÄLEN-MORA",
        "ORG.NR. 884401-5886",
        "VASALOPPETS HUS, SE-792 32 MORA",
        "INFO@VASALOPPET.SE  TEL: +46 250-392 00",
        "WWW.VASALOPPET.SE",
    ];
    for (const f of footer) { line(f, { font: "16px sans-serif", align: "center" }); }
    gap(12);

    // Beskär till använd höjd (jämnt antal rader)
    const usedH = Math.min(canvas.height, Math.ceil(y));
    const out = document.createElement("canvas");
    out.width = W;
    out.height = usedH;
    out.getContext("2d").drawImage(canvas, 0, 0);
    return out;
};

// ---------- ESC/POS raster ----------

/** Bygger ESC/POS-byteström (GS v 0) från canvas, bandad i höjdled. */
const canvasToEscPos = (canvas: HTMLCanvasElement): Uint8Array => {
    const w = canvas.width, h = canvas.height;
    const ctx = canvas.getContext("2d");
    const px = ctx.getImageData(0, 0, w, h).data;
    const bytesPerRow = Math.ceil(w / 8);

    const chunks: number[] = [0x1b, 0x40]; // ESC @ (init)
    const BAND = 128;
    for (let y0 = 0; y0 < h; y0 += BAND) {
        const rows = Math.min(BAND, h - y0);
        chunks.push(0x1d, 0x76, 0x30, 0x00,
            bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff,
            rows & 0xff, (rows >> 8) & 0xff);
        for (let y = 0; y < rows; y++) {
            for (let b = 0; b < bytesPerRow; b++) {
                let byte = 0;
                for (let bit = 0; bit < 8; bit++) {
                    const x = b * 8 + bit;
                    if (x < w) {
                        const idx = ((y0 + y) * w + x) * 4;
                        const a = px[idx + 3];
                        const lum = a < 128 ? 255 : (0.299 * px[idx] + 0.587 * px[idx + 1] + 0.114 * px[idx + 2]);
                        if (lum < 128) { byte |= (0x80 >> bit); }
                    }
                }
                chunks.push(byte);
            }
        }
    }
    chunks.push(0x1b, 0x64, 0x04); // mata fram 4 rader
    return Uint8Array.from(chunks);
};

/** Renderar och skriver ut ett kvitto. Kastar fel om något går snett. */
export const printReceipt = async (data: ReceiptData): Promise<void> => {
    await ensureConnected();
    const canvas = await buildReceiptCanvas(data);
    const bytes = canvasToEscPos(canvas);
    await writeBytes(bytes);
};
